import React, { useState, useEffect, useRef } from 'react';
import { Quote, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';

const TestimonialCard = ({ testimonial, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 200;
  const isLong = testimonial.text.length > maxLength;
  const displayText = isExpanded ? testimonial.text : testimonial.text.slice(0, maxLength);

  return (
    <div className={`group relative h-full ${index % 3 === 0 ? 'lg:col-span-2' : ''}`}>
      {/* Glow Effect */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition duration-500 blur-xl"></div>

      {/* Card Content */}
      <div className="relative h-full glass-effect rounded-2xl p-6 sm:p-8 flex flex-col hover:border-cyan-500/30 transition-all duration-300">
        {/* Background Decorative Quote */}
        <Quote className="absolute top-4 right-4 w-24 h-24 text-white/5 opacity-10 pointer-events-none" />

        {/* Brand/Company Identifier */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6 w-fit">
          <MessageSquare className="w-3 h-3 text-cyan-400" />
          <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-400">
            {testimonial.logo}
          </span>
        </div>

        {/* Quote Content */}
        <div className="relative z-10 flex-grow">
          <p className="text-gray-200 text-base sm:text-lg leading-relaxed italic">
            "{displayText}{!isExpanded && isLong && '...'}"
          </p>
          {isLong && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-4 text-cyan-400 hover:text-cyan-300 text-sm font-semibold transition-colors"
            >
              {isExpanded ? 'Show Less' : 'Read More'}
            </button>
          )}
        </div>

        {/* Footer info */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold text-xl uppercase shadow-lg shadow-cyan-500/20">
              {testimonial.name.charAt(0)}
            </div>
            <div>
              <h4 className="text-white font-bold text-lg leading-tight">{testimonial.name}</h4>
              <p className="text-gray-400 text-sm">{testimonial.title}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  const testimonials = [
    {
      name: "Arun Janarthanan",
      title: "CavinKare Private Limited",
      text: "The endorsement deal with Akshay Kumar for Indica Hair Colour wouldn't have been possible without Centre Stage. They brought in-depth industry knowledge, strong relationships, and a hands-on approach that made all the difference. What impressed us most was their ability to balance professionalism with a warm, personal touch, ensuring the collaboration felt effortless.",
      logo: "CavinKare"
    },
    {
      name: "Ravi Shankar",
      title: "Chief Brand & Communication Officer at LifeCell International Pvt. Ltd",
      text: "Whenever I narrate or recollect the Aishwarya Rai enrolment story to anyone, I don't miss to mention about you to them. You are an integral part of this epic event.",
      logo: "LifeCell"
    },
    {
      name: "Rajiv Hingoo",
      title: "MD & COO – Jefferies India",
      text: "Centre stage is an absolute pleasure to work with. Their attention to detail and meticulous planning makes the coordination process straightforward and efficient. They have a great understanding of the industry and their passion for creating successful events shines through in every detail. Their team is dedicated and supportive, ensuring a positive experience for all partners involved. I highly recommend them for all types of events and sporting connects. Wishing them all the very best!",
      logo: "Jefferies"
    },
    {
      name: "Nitin Raghuvanshi",
      title: "Chief Operating Officer at Ajivasan Group",
      text: "We were introduced to the Centre Stage team regarding our flagship event - Ajivasan ACT. We thank the team for executing sponsorships from household names like Dabur and Everest from start to end, ensuring all deliverables were met on time. We look forward to working with them again in the future!",
      logo: "Ajivasan"
    }
  ];

  const handleScroll = () => {
    if (scrollRef.current) {
      const index = Math.round(scrollRef.current.scrollLeft / scrollRef.current.offsetWidth);
      setActiveIndex(index);
    }
  };

  return (
    <section id="testimonials" className="py-24 bg-[#0a0f1a] relative overflow-hidden">
      {/* Ambient background glows */}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-cyan-500/10 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-purple-500/10 blur-[120px] rounded-full"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header section with refined typography */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight">
              Tails of <span className="text-cyan-400 font-playfair italic font-medium">Excellence</span>
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-cyan-400 to-purple-500 mt-6 mb-4"></div>
            <p className="text-gray-400 text-lg">
              Partnering with industry pioneers to deliver world-class experiences.
            </p>
          </div>

          {/* Custom stats badge */}
          <div className="hidden md:block bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-md">
            <div className="text-3xl font-bold text-white mb-1">30+ Years</div>
            <div className="text-cyan-400 font-bold tracking-tighter uppercase text-xs">Industry Mastery</div>
          </div>
        </div>

        {/* Testimonials Desktop Masonry Grid */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
          {testimonials.map((t, i) => (
            <TestimonialCard key={i} testimonial={t} index={i} />
          ))}
        </div>

        {/* Testimonials Mobile Carousel */}
        <div className="md:hidden">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 pb-8"
          >
            {testimonials.map((t, i) => (
              <div key={i} className="min-w-full snap-center">
                <TestimonialCard testimonial={t} index={i} />
              </div>
            ))}
          </div>

          {/* Carousel indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {testimonials.map((_, i) => (
              <div
                key={i}
                className={`h-1 transition-all duration-300 rounded-full ${i === activeIndex ? 'w-8 bg-cyan-400' : 'w-2 bg-white/20'}`}
              ></div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};
